import { config } from "../config";
import { HttpError, toHttpError } from "./httpError";

export type QueryParams = Record<
  string,
  string | number | boolean | undefined | null
>;

export type HttpClientOptions = {
  authorisation?: string;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  baseUrl?: string;
};

const buildQuery = (query?: string | QueryParams): string => {
  if (!query) return "";
  if (typeof query === "string") return query;

  const search = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      search.append(key, String(value));
    }
  });

  const serialised = search.toString();
  return serialised ? `?${serialised}` : "";
};

const buildHeaders = (options: HttpClientOptions, accept: string) => {
  const headers: Record<string, string> = {
    Accept: accept,
    "Content-Type": "application/json",
    ...options.headers,
  };

  if (options.authorisation) {
    headers.Authorization = `Bearer ${options.authorisation}`;
  }

  return headers;
};

const readJson = async <T,>(response: Response, url: string): Promise<T> => {
  const raw = await response.text();
  if (!raw) return undefined as T;

  try {
    return JSON.parse(raw) as T;
  } catch {
    throw HttpError.parse(url, raw);
  }
};

// The single place that owns the status check. Every response goes through
// here, so no call site can opt out of it.
const request = async <T,>(
  url: string,
  init: RequestInit,
  parse: "json" | "blob"
): Promise<T> => {
  let response: Response;

  try {
    response = await fetch(url, init);
  } catch (cause) {
    throw HttpError.network(url, cause);
  }

  if (!response.ok) {
    throw await toHttpError(response, url);
  }

  if (response.status === 204) return undefined as T;

  return parse === "blob"
    ? ((await response.blob()) as T)
    : readJson<T>(response, url);
};

const resolveUrl = (
  endpoint: string,
  options: HttpClientOptions,
  query?: string | QueryParams
): string => {
  const baseUrl = options.baseUrl ?? config.REACT_APP_PROXY_BASE_URL;

  if (!baseUrl) {
    throw HttpError.config(
      endpoint,
      "REACT_APP_PROXY_BASE_URL is not configured"
    );
  }

  return `${baseUrl}/${endpoint}${buildQuery(query)}`;
};

export default function httpClient(
  endpoint: string,
  options: HttpClientOptions = {}
) {
  return {
    get: <T,>(query?: string | QueryParams): Promise<T> =>
      request<T>(
        resolveUrl(endpoint, options, query),
        {
          method: "GET",
          headers: buildHeaders(options, "application/json"),
          signal: options.signal,
        },
        "json"
      ),

    getBlob: (query?: string | QueryParams): Promise<Blob> =>
      request<Blob>(
        resolveUrl(endpoint, options, query),
        {
          method: "GET",
          headers: buildHeaders(options, "application/json"),
          signal: options.signal,
        },
        "blob"
      ),

    post: <T,>(body?: unknown): Promise<T> =>
      request<T>(
        resolveUrl(endpoint, options),
        {
          method: "POST",
          headers: buildHeaders(options, "*/*"),
          signal: options.signal,
          ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
        },
        "json"
      ),
  };
}

export function authApi() {
  return {
    get: <T,>(): Promise<T> => {
      const authUrl = config.REACT_APP_AUTH_URL;

      if (!authUrl) {
        throw HttpError.config("token", "REACT_APP_AUTH_URL is not configured");
      }

      const url = `${authUrl}/token?grant_type=client_credentials`;

      return request<T>(
        url,
        {
          method: "GET",
          headers: {
            Authorization:
              "Basic " +
              btoa(`${config.REACT_APP_USERNAME}:${config.REACT_APP_KEY}`),
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        },
        "json"
      );
    },
  };
}
